/**
 * pages/procurement/BudgetsListPage.jsx — Budgets register.
 * Lists budgets with KPI tiles + a create drawer.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Eye, Trash2, Sparkles, Loader2 } from 'lucide-react';

import MainLayout from '../../components/MainLayout';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/data-table';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import { BudgetDrawer } from '../../components/procurement/BudgetDrawer';
import { BudgetCopilotPanel } from '../../components/procurement/BudgetCopilotPanel';
import { listBudgets, deleteBudget, getBudgetFilterOptions, HORIZONS } from '../../api/budgets';
import { getCurrentAccount } from '../../lib/auth';

const ALL = '__all__';

const fmtMoney = (n) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
const horizonLabel = (m) => HORIZONS.find((h) => h.value === m)?.label || `${m} mo`;

export default function BudgetsListPage() {
  const navigate = useNavigate();
  const account = getCurrentAccount();
  const accountId = account?.id;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [options, setOptions] = useState({ created_by: [], commodities: [], years: [] });
  const [fBy, setFBy] = useState(ALL);
  const [fCommodity, setFCommodity] = useState(ALL);
  const [fYear, setFYear] = useState(ALL);
  const limit = 25;

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const data = await listBudgets({
        accountId, limit, offset,
        created_by: fBy !== ALL ? fBy : undefined,
        commodity: fCommodity !== ALL ? fCommodity : undefined,
        year: fYear !== ALL ? fYear : undefined,
      });
      setRows(data.items);
      setTotal(data.total);
    } catch (e) {
      toast.error('Failed to load budgets', { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, [accountId, offset, fBy, fCommodity, fYear]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (accountId) getBudgetFilterOptions({ accountId }).then(setOptions).catch(() => {});
  }, [accountId]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete budget "${row.name}"? Scenarios and baseline will be removed.`)) return;
    try {
      await deleteBudget({ accountId, id: row.id });
      toast.success('Budget deleted');
      load();
    } catch (e) {
      toast.error('Delete failed', { description: e?.response?.data?.detail || e.message });
    }
  };

  const columns = [
    { key: 'name', header: 'Budget', accessor: (r) => (
      <button onClick={() => navigate(`/procurement/budgets/${r.id}`)} className="font-medium hover:text-primary text-left" data-testid={`budget-link-${r.id}`}>
        <span className="inline-flex items-center gap-1.5">
          {r.name}
          {r.build_status === 'building' || r.build_status === 'queued' ? (
            <Badge variant="secondary" className="text-[10px]"><Loader2 className="w-2.5 h-2.5 mr-1 animate-spin" />Building</Badge>
          ) : r.build_status === 'failed' ? (
            <Badge variant="destructive" className="text-[10px]">Build failed</Badge>
          ) : null}
        </span>
        {r.description && <div className="text-xs text-muted-foreground font-normal">{r.description}</div>}
      </button>
    ) },
    { key: 'horizon', header: 'Horizon', accessor: (r) => <Badge variant="outline">{horizonLabel(r.horizon_months)}</Badge> },
    { key: 'start', header: 'Start', accessor: (r) => <span className="text-sm">{r.start_month || '—'}</span> },
    { key: 'commodities', header: 'Commodities', accessor: (r) => (
      <span className="text-sm capitalize">{(r.commodities || []).length ? r.commodities.join(', ') : 'All'}</span>
    ) },
    { key: 'esc', header: 'Escalation', accessor: (r) => <span className="text-sm">{r.escalation_pct}%</span> },
  ];

  return (
    <MainLayout>
      <div className="max-w-[1400px] mx-auto space-y-6">
        <PageHeader
          title="Budgets"
          description="Build forward-looking commodity budgets from historical usage, split supply vs delivery, and model scenarios."
          actions={
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setCopilotOpen(true)} data-testid="budgets-copilot-btn">
                <Sparkles className="w-4 h-4 mr-1.5 text-violet-600" /> Ask Beacon
              </Button>
              <Button onClick={() => setDrawerOpen(true)} data-testid="budgets-new-btn">
                <Plus className="w-4 h-4 mr-1.5" /> New Budget
              </Button>
            </div>
          }
        />

        <div className="flex items-center gap-2 flex-wrap" data-testid="budgets-filters">
          <Select value={fBy} onValueChange={(v) => { setOffset(0); setFBy(v); }}>
            <SelectTrigger className="w-48" data-testid="budgets-filter-createdby"><SelectValue placeholder="Created by" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All creators</SelectItem>
              {options.created_by.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fCommodity} onValueChange={(v) => { setOffset(0); setFCommodity(v); }}>
            <SelectTrigger className="w-44" data-testid="budgets-filter-commodity"><SelectValue placeholder="Commodity" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All commodities</SelectItem>
              {options.commodities.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={fYear} onValueChange={(v) => { setOffset(0); setFYear(v); }}>
            <SelectTrigger className="w-36" data-testid="budgets-filter-year"><SelectValue placeholder="Created year" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All years</SelectItem>
              {options.years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          {(fBy !== ALL || fCommodity !== ALL || fYear !== ALL) && (
            <Button variant="ghost" size="sm" onClick={() => { setOffset(0); setFBy(ALL); setFCommodity(ALL); setFYear(ALL); }} data-testid="budgets-filter-clear">
              Clear
            </Button>
          )}
        </div>

        <DataTable
          columns={columns}
          rows={rows}
          total={total}
          offset={offset}
          limit={limit}
          loading={loading}
          onPageChange={setOffset}
          exportFilename={`budgets-${accountId}.csv`}
          testId="budgets-table"
          emptyTitle="No budgets yet"
          emptyHint="Create your first budget — we'll build a baseline from your historical bills automatically."
          emptyAction={<Button size="sm" onClick={() => setDrawerOpen(true)} data-testid="budgets-empty-cta"><Plus className="w-4 h-4 mr-1.5" /> New Budget</Button>}
          rowActions={(r) => (
            <div className="flex justify-end gap-0.5">
              <Button size="sm" variant="ghost" onClick={() => navigate(`/procurement/budgets/${r.id}`)} title="Open" data-testid={`budget-open-${r.id}`}><Eye className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => handleDelete(r)} title="Delete" data-testid={`budget-delete-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          )}
        />
      </div>

      <BudgetDrawer open={drawerOpen} onOpenChange={setDrawerOpen} accountId={accountId}
        onSaved={(created) => { load(); if (created?.id) navigate(`/procurement/budgets/${created.id}`); }} />
      <BudgetCopilotPanel open={copilotOpen} onOpenChange={setCopilotOpen} accountId={accountId} onChanged={load} />
    </MainLayout>
  );
}
